import { rd } from '@passionware/monads';
import React from 'react';
import { SHELL_FEATURE_FLAGS } from 'v5/core/app-scope/shellFeatureFlags';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import {
  FeatureName,
  FeatureValueById,
  FeatureEntryById,
  FeatureGroupDefinition
} from 'v5/platform/feature-management/feature-management';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithServices } from 'v5/platform/ts/services';
/**
 * Extracts all possible values from a feature flag spec as a union type.
 * - For boolean specs: returns `true | false`
 * - For enum specs: returns the union of all values in the options array
 */
type FeatureFlagSpecPossibleValues<Spec> = Spec extends {
  type: 'enum';
  options: readonly (infer U)[];
}
  ? U
  : Spec extends { type: 'boolean' }
    ? boolean
    : never;

/**
 * Get all possible values for a feature by its ID.
 */
type FeaturePossibleValuesById<
  Config extends FeatureGroupDefinition,
  Id extends FeatureName<Config>
> = FeatureFlagSpecPossibleValues<FeatureEntryById<Config, Id>['flagSpec']>;

/**
 * Higher-order component that conditionally renders components based on feature flag values.
 * Works with both boolean and enum feature flags.
 *
 * @param featureName - The name of the feature flag to check
 * @param componentMap - Mapping of feature flag values to their corresponding components
 * @param fallbackComponent - Optional fallback component if the feature value is not in the map
 *
 * @example
 * Boolean feature flag:
 * ```tsx
 * const ResourcingRoute = withFeatureFallback('new-production-resourcing', {
 *   true: ResourcingTabConnected,
 *   false: ProductionResourcingWithBanner
 * });
 * ```
 *
 * @example
 * Enum feature flag:
 * ```tsx
 * const ResourcePaneRoute = withFeatureFallback('resource-pane', {
 *   new: LegacyResourcePane,
 *   v5: ResourcePaneV5
 * });
 * ```
 */
export function withFeatureFallback<
  TProps extends object,
  TFeature extends FeatureName<AtellioFeatureGroupDefinition>
>(
  featureName: TFeature,
  componentMap: {
    [K in FeaturePossibleValuesById<AtellioFeatureGroupDefinition, TFeature> &
      PropertyKey]: React.ComponentType<TProps>;
  },
  fallbackComponent?: React.ComponentType<TProps>
): React.ComponentType<TProps> {
  function FeatureFallbackWrapper(
    props: TProps & WithServices<[WithFeatureService, WithAppScopeService]>
  ) {
    const browsingScope =
      props.services.appScopeService.useResolvedScopeIdentity();
    const organizationScope =
      props.services.appScopeService.useOrganizationScopeIdentity();
    const scope = SHELL_FEATURE_FLAGS.has(featureName)
      ? organizationScope
      : browsingScope;
    const featureFlag = props.services.featureService.useFeature(
      scope,
      featureName
    );
    const featureValue = rd.getOrElse(featureFlag, null) as FeatureValueById<
      AtellioFeatureGroupDefinition,
      TFeature
    >;

    // Use the feature value directly as the key (works for both booleans and strings)
    const Component =
      componentMap[
        featureValue as FeaturePossibleValuesById<
          AtellioFeatureGroupDefinition,
          TFeature
        > &
          PropertyKey
      ];

    if (Component) {
      // Type assertion needed because TypeScript can't prove all components in the map accept TProps
      const ComponentTyped = Component as React.ComponentType<TProps>;
      return <ComponentTyped {...props} />;
    }

    // If no matching component and fallback is provided, use fallback
    if (fallbackComponent) {
      const FallbackComponent = fallbackComponent;
      return <FallbackComponent {...props} />;
    }

    // If no component found and no fallback, render nothing or show error in dev
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `withFeatureFallback: No component found for feature "${featureName}" with value "${featureValue}"`
      );
    }

    return null;
  }

  return FeatureFallbackWrapper as React.ComponentType<TProps>;
}
